import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Property } from 'app/modules/projects/projects.types';
import { PropertyTenantsService } from '../../tenants.service';
import { ProjectType, PropertyType, ProjectStage } from 'app/core/models/account.types';
import { CurrencyMaskOptions, getCurrencyMask } from 'app/shared/Utils/common.utils';
import { AccountService } from 'app/core';
import { PropertyUnit } from 'app/modules/projects/details/tenants/tenants.types';
import { TsRxjsOperators } from '@shared/Utils';
import { MessagesUtils } from '@shared/Utils/messages.utils';
import { TsSnackbarService } from 'tsui';

@Component({
  selector: 'property-tenants-add-tenant',
  templateUrl: './add-tenant.component.html',
  styleUrls: ['./add-tenant.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PropertyTenantsAddTenantComponent extends OnDestroyMixin implements OnInit {
  propertyTenantForm: UntypedFormGroup;
  projectTypes: ProjectType[];
  propertyTypes: PropertyType[];
  projectStages: ProjectStage[];
  property: Property;
  propertyUnit: PropertyUnit;
  public uploadedFile: any;
  public isLoading: boolean;
  options: [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly matDialogRef: MatDialogRef<PropertyTenantsAddTenantComponent>,
    private _accountService: AccountService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly propertyTenantsService: PropertyTenantsService,
    private readonly snackbarService: TsSnackbarService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.buildForm();

    if (this.isPropertyLease) {
      this.propertyTenantForm.addControl('name', this.formBuilder.control(`${this.data?.property?.name} Lease` || ''));
    }
  }

  protected get isPropertyLease(): boolean {
    return this.propertyUnit == null;
  }

  private buildForm(): void {
    this.propertyTenantForm = this.formBuilder.group({
      id: [''],
      lease_start_date: ['', [Validators.required]],
      lease_end_date: ['', [Validators.required]],
      base_monthly_rent: [null, [Validators.required]],
    });

    this.property = this.data.property;
    this.propertyUnit = this.data.propertyUnit;
    if (this.data.propertyTenant) this.propertyTenantForm.patchValue(this.data.propertyTenant);
  }

  public onFileChanged($event): void {
    let file = $event.target.files[0];
    this.uploadedFile = file;
  }

  public discard(): void {
    this.matDialogRef.close();
  }

  private get accountCurrency(): string {
    return this._accountService.getCurrentAccount()?.display_currency;
  }

  get currencyMaskOptions(): CurrencyMaskOptions {
    return getCurrencyMask(this.accountCurrency, 2);
  }

  public save(): void {
    this.propertyTenantForm.disable();
    let propertyTenant = this.propertyTenantForm.value;

    if (this.isPropertyLease) {
      this.handlePropertyLease(propertyTenant);
    } else {
      propertyTenant.property_unit_id = this.propertyUnit.id;
      this.handlePropertyUnitLease(propertyTenant);
    }
  }

  private handlePropertyLease(propertyTenant: any) {
    this.propertyTenantsService
      .createPropertyLease(this.property.id, propertyTenant)
      .pipe(
        untilComponentDestroyed(this),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbarService,
          MessagesUtils.Success.CREATE('Lease'),
          MessagesUtils.Error.CREATE('Lease'),
        ),
      )
      .subscribe(() => this.matDialogRef.close(true));
  }

  private handlePropertyUnitLease(propertyTenant: any) {
    if (this.data.propertyTenant && this.data.propertyTenant.id) {
      this.propertyTenantsService
        .updatePropertyTenant(this.propertyUnit.id, propertyTenant)
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => this.discard());
    } else {
      this.propertyTenantsService
        .createPropertyTenant(this.propertyUnit.id, propertyTenant)
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => this.discard());
    }
  }

  // Function attached to debounce-click directive to flag the button's disable state.
  public isDebounceLoading(isLoading: boolean): boolean {
    return (this.isLoading = isLoading);
  }

  public get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.propertyTenantForm.pristine || this.propertyTenantForm.invalid;
  }
}

/*
let propertyTenant = {
      name: '',
      lease_start_date: '',
      lease_end_date: '',
      occupancy_start_date: '',
      occupancy_end_date: '',
      description: '',
    };
    this.propertyTenantsService
      .createPropertyTenant(this.propertyUnit.id, propertyTenant)
      .pipe(untilComponentDestroyed(this))
      .subscribe();
      */
